/**
 * Generic RBAC Permission Utility
 *
 * This utility provides permission checking functionality that can be used
 * across different modules (materials, assignments, etc.)
 *
 * Usage:
 *   import { hasPermission, MATERIAL_PERMISSIONS } from '@core/utils/permissions';
 *
 *   // In a selector or component:
 *   const canEdit = hasPermission(state, MATERIAL_PERMISSIONS.EDIT, isInstructor);
 */

import { AulaState } from '../types/state';
import { getCurrentSpaceId } from '../selectors/space';
import { currentUserId } from '../selectors/user';
import { getLaunchDarklyFeatures } from '../selectors/institution';

// =============================================================================
// Permission Keys by Module
// =============================================================================

export const MATERIAL_PERMISSIONS = {
  VIEW: 'material:view',
  CREATE: 'material:create',
  EDIT: 'material:edit',
  DELETE: 'material:delete',
  IMPORT: 'material:import',
  EXPORT: 'material:export',
  RESTORE: 'material:restore',
  PUBLISH: 'material:publish',
  HISTORY: 'material:history',
  EDUCATOR_AREA: 'material:educatorArea',
  PROGRESS: 'material:progress',
} as const;

// Future modules can add their permission keys here:
// export const ASSIGNMENT_PERMISSIONS = { ... } as const;
// export const POST_PERMISSIONS = { ... } as const;

export type MaterialPermissionKey =
  typeof MATERIAL_PERMISSIONS[keyof typeof MATERIAL_PERMISSIONS];

// =============================================================================
// Permission Denied Messages
// =============================================================================

const PERMISSION_DENIED_MESSAGES: Record<string, string> = {
  // Material permissions
  [MATERIAL_PERMISSIONS.VIEW]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.CREATE]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.EDIT]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.DELETE]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.IMPORT]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.EXPORT]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.RESTORE]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.PUBLISH]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.HISTORY]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.EDUCATOR_AREA]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
  [MATERIAL_PERMISSIONS.PROGRESS]:
    'This action isn’t available for your role. If you feel you need access, your admin can help you',
};

/**
 * Get user-friendly message for permission denial
 */
export const getPermissionDeniedMessage = (permissionKey: string): string => {
  return (
    PERMISSION_DENIED_MESSAGES[permissionKey] ||
    'This action isn’t available for your role. If you feel you need access, your admin can help you'
  );
};

// =============================================================================
// Role Data Types
// =============================================================================

type RolePermission = string | { key?: string; id?: string };

type RoleData = {
  id?: string;
  name?: string;
  key?: string;
  permissions?: RolePermission[];
};

type SpaceMembership = {
  classRoom?: string;
  user?: string;
  roleDetails?: RoleData; // Role data from API
  isInstructor?: boolean;
};

// =============================================================================
// Permission Extraction Helpers
// =============================================================================

/**
 * Extract permission keys from a role object
 * Supports both new format (array of strings) and old format (array of objects with key property)
 */
const extractPermissionsFromRole = (role?: RoleData): string[] => {
  if (!role?.permissions || !Array.isArray(role.permissions)) {
    return [];
  }

  return role.permissions
    .map((p) => {
      if (typeof p === 'string') return p;
      if (p && typeof p.key === 'string') return p.key;
      return null;
    })
    .filter((key): key is string => typeof key === 'string' && key.length > 0);
};

/**
 * Get role data from membership
 */
const getRoleFromMembership = (
  membership: SpaceMembership | null
): RoleData | null => {
  if (!membership) {
    return null;
  }
  return membership.roleDetails || null;
};

/**
 * Get the current user's membership in the current space
 */
const getCurrentSpaceMembership = (
  state: AulaState
): SpaceMembership | null => {
  const spaceId = getCurrentSpaceId(state);
  const userId = currentUserId(state);

  if (!spaceId || !userId) {
    return null;
  }

  const membershipIds = (state.classRoomUser?.self as string[]) || [];
  const membershipMap =
    (state.data?.classRoomUsers as Record<string, SpaceMembership>) || {};

  const membership = membershipIds
    .map((id) => membershipMap[id])
    .find(
      (m) => m && m.classRoom === spaceId && (!m.user || m.user === userId)
    );

  return membership || null;
};

/**
 * Get all permission keys for the current user in the current space
 */
export const getUserPermissionsInCurrentSpace = (
  state: AulaState
): string[] => {
  const membership = getCurrentSpaceMembership(state);
  const role = getRoleFromMembership(membership);

  if (!role) {
    return [];
  }

  return extractPermissionsFromRole(role);
};

// =============================================================================
// Main Permission Check Function
// =============================================================================

/**
 * Check if the current user has a specific permission in the current space
 *
 * @param state - Redux state
 * @param permissionKey - The permission to check (e.g., 'material:create')
 * @param fallback - Value to return if RBAC data is not available (defaults to false)
 * @returns boolean - Whether the user has the permission
 *
 * @example
 * // In a selector:
 * const canCreate = hasPermission(state, MATERIAL_PERMISSIONS.CREATE, isInstructor(state));
 */
export const hasPermission = (
  state: AulaState,
  permissionKey: string,
  fallback: boolean = false
): boolean => {
  // If RBAC is disabled (feature flag is false), ignore permissions and use fallback
  if (!isRbacEnabled(state)) {
    return fallback;
  }

  const membership = getCurrentSpaceMembership(state);
  const role = getRoleFromMembership(membership);

  // If no membership or role data, use fallback (legacy behavior)
  if (!role) {
    return fallback;
  }

  const permissions = extractPermissionsFromRole(role);

  // If role exists but has no permissions array, use fallback
  if (permissions.length === 0) {
    return fallback;
  }

  return permissions.includes(permissionKey);
};

/**
 * Check if RBAC is enabled for the current user in current space
 * First checks the WebAppFtForRBAC202505 feature flag.
 * If the flag is false, RBAC is disabled (legacy behavior).
 * If the flag is true, checks if the user has role data with permissions.
 */
export const isRbacEnabled = (state: AulaState): boolean => {
  const features = getLaunchDarklyFeatures(state);
  const rbacFeatureFlag = features.webAppFtForRbac202505;

  // If feature flag is false, RBAC is disabled (legacy behavior)
  if (!rbacFeatureFlag) {
    return false;
  }

  // If feature flag is true, check if user has role data with permissions
  const membership = getCurrentSpaceMembership(state);
  const role = getRoleFromMembership(membership);

  const hasPermissions = Boolean(
    role?.permissions &&
      Array.isArray(role.permissions) &&
      role.permissions.length > 0
  );

  return hasPermissions;
};

// =============================================================================
// Convenience Functions for Material Module
// =============================================================================

/**
 * Check if user can create materials in current space
 */
export const canCreateMaterial = (
  state: AulaState,
  fallbackIsInstructor: boolean
): boolean => {
  return hasPermission(
    state,
    MATERIAL_PERMISSIONS.CREATE,
    fallbackIsInstructor
  );
};

/**
 * Check if user can edit materials in current space
 */
export const canEditMaterial = (
  state: AulaState,
  fallbackIsInstructor: boolean
): boolean => {
  return hasPermission(state, MATERIAL_PERMISSIONS.EDIT, fallbackIsInstructor);
};

/**
 * Check if user can delete materials in current space
 */
export const canDeleteMaterial = (
  state: AulaState,
  fallbackIsInstructor: boolean
): boolean => {
  return hasPermission(
    state,
    MATERIAL_PERMISSIONS.DELETE,
    fallbackIsInstructor
  );
};

/**
 * Check if user can view materials in current space
 * Note: View permission typically defaults to true for all users
 */
export const canViewMaterial = (
  state: AulaState,
  fallback: boolean = true
): boolean => {
  return hasPermission(state, MATERIAL_PERMISSIONS.VIEW, fallback);
};

// =============================================================================
// Permission Action Creators (for use with dispatch)
// =============================================================================

/**
 * Create a toast action for permission denial
 * Use with dispatch: dispatch(createPermissionDeniedToast(MATERIAL_PERMISSIONS.EDIT))
 */
export const createPermissionDeniedToast = (permissionKey: string) => ({
  message: getPermissionDeniedMessage(permissionKey),
});

// =============================================================================
// Generic permission evaluation helper
// =============================================================================

export type PermissionEvaluationInput = {
  /**
   * The permission key that should be satisfied (e.g. material:create).
   */
  permission: string;
  /**
   * Optional explicit list of permission keys already available to the user.
   */
  permissions?: ReadonlyArray<string>;
  /**
   * Optional state reference used to resolve the current user's permissions.
   */
  state?: AulaState;
  /**
   * Optional fallback value when no permission data is available.
   */
  fallback?: boolean;
  /**
   * Human friendly description of the action (used in the failure message).
   */
  actionLabel?: string;
};

export type PermissionEvaluationResult = {
  allowed: boolean;
  message?: string;
};

export const evaluatePermission = ({
  permission,
  permissions,
  state,
  fallback = false,
  actionLabel,
}: PermissionEvaluationInput): PermissionEvaluationResult => {
  // If RBAC is disabled (feature flag is false), ignore permissions and use fallback
  if (state && !isRbacEnabled(state)) {
    if (fallback) {
      return { allowed: true };
    }
    const baseMessage = getPermissionDeniedMessage(permission);
    return {
      allowed: false,
      message: baseMessage,
    };
  }

  let resolvedPermissions = permissions;

  if ((!resolvedPermissions || resolvedPermissions.length === 0) && state) {
    resolvedPermissions = getUserPermissionsInCurrentSpace(state);
  }

  let allowed = false;
  if (resolvedPermissions && resolvedPermissions.length > 0) {
    allowed = resolvedPermissions.includes(permission);
  } else {
    allowed = fallback;
  }

  if (allowed) {
    return { allowed: true };
  }

  const baseMessage = getPermissionDeniedMessage(permission);
  return {
    allowed: false,
    message: baseMessage,
  };
};
